import { useEffect } from "react";

import {
  Chip,
  ContentCard,
  LoadingSkeleton,
  Radio,
} from "@narmi/design_system";
import type { DeliverySpeedOption } from "../ach/useDeliverySpeedOptions";
import styles from "../ach/DeliverySpeedSection.module.scss";

type InvoiceDeliverySpeedCardsProps = {
  options: DeliverySpeedOption[];
  selectedValue: string;
  onSelect: (value: string) => void;
  /** true while the available options are still being resolved (aka checking for fednow eligibility). */
  isResolvingOptions?: boolean;
};

const DeliverySpeedCardContent = ({
  option,
}: {
  option: DeliverySpeedOption;
}) => (
  <div className={styles.radioCardContent}>
    <div
      className={`${styles.radioCardHeader} ${styles.radioCardHeaderStacked}`}
    >
      <div className={styles.radioCardHeaderLeft}>
        <p className={styles.radioCardLabel}>{option.label}</p>
        {option.badgeLabel ? (
          <span className={styles.recommendedChip}>
            <Chip kind="success" label={option.badgeLabel} />
          </span>
        ) : null}
      </div>
      <p className={styles.radioCardDescription}>
        <span className={styles.radioCardDescriptionArrival}>
          {option.arrival}
        </span>
        <span aria-hidden="true" className={styles.radioCardDescriptionText}>
          {" "}
          ·{" "}
        </span>
        <span className={styles.radioCardDescriptionFee}>{option.fee}</span>
      </p>
    </div>
  </div>
);

const InvoiceDeliverySpeedCards = ({
  options,
  selectedValue,
  onSelect,
  isResolvingOptions = false,
}: InvoiceDeliverySpeedCardsProps) => {
  const onlyOption = options.length === 1 ? options[0] : null;

  useEffect(() => {
    if (
      !isResolvingOptions &&
      onlyOption &&
      selectedValue !== onlyOption.value
    ) {
      onSelect(onlyOption.value);
    }
  }, [isResolvingOptions, onlyOption, selectedValue, onSelect]);

  // loading state until isResolvingOptions is done
  if (isResolvingOptions) {
    return (
      <LoadingSkeleton isLoading>
        <ContentCard kind="bordered">
          <div>
            <div className={styles.radioCardContent}>
              <div className={styles.radioCardLabel}>&nbsp;</div>
              <div className={styles.radioCardDescription}>&nbsp;</div>
            </div>
          </div>
        </ContentCard>
      </LoadingSkeleton>
    );
  }

  if (onlyOption) {
    return (
      <ContentCard kind="bordered">
        <div className={styles.staticCard}>
          <DeliverySpeedCardContent option={onlyOption} />
        </div>
      </ContentCard>
    );
  }

  return (
    <div className={styles.deliverySpeedSelectContainer}>
      {options.map((option) => (
        <Radio
          key={option.value}
          name="delivery-speed"
          value={option.value}
          kind="input-card"
          checked={selectedValue === option.value}
          onCheck={() => onSelect(option.value)}
        >
          <DeliverySpeedCardContent option={option} />
        </Radio>
      ))}
    </div>
  );
};

export default InvoiceDeliverySpeedCards;
